import { type InputHTMLAttributes, type ReactNode, forwardRef, useId, useState } from 'react'
import { Eye, EyeOff, X } from 'lucide-react'
import { cn } from '@/shared/utils/cn'

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  helperText?: string
  error?: string
  leading?: ReactNode
  trailing?: ReactNode
  clearable?: boolean
  onClear?: () => void
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label,
      helperText,
      error,
      leading,
      trailing,
      clearable,
      onClear,
      type = 'text',
      className,
      disabled,
      value,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId()
    const inputId = id ?? generatedId
    const describedBy = error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
    const [visible, setVisible] = useState(false)
    const isPassword = type === 'password'
    const resolvedType = isPassword && visible ? 'text' : type

    return (
      <div className="flex flex-col gap-1.5 sm:gap-2">
        {label && (
          <label htmlFor={inputId} className="text-preset-label text-text-muted">
            {label}
          </label>
        )}
        <div
          className={cn(
            'flex h-11 items-center gap-2 rounded-md border bg-surface-subtle px-2.5 text-text shadow-input transition duration-base focus-within:border-brand focus-within:bg-surface focus-within:ring-2 focus-within:ring-brand/15 sm:h-12 sm:px-3',
            error ? 'border-danger' : 'border-transparent',
            disabled && 'bg-gray-100 text-text-subtle',
            className,
          )}
        >
          {leading && (
            <span className="flex size-[18px] items-center justify-center text-text-subtle sm:size-5">
              {leading}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            type={resolvedType}
            disabled={disabled}
            value={value}
            aria-invalid={!!error}
            aria-describedby={describedBy}
            className="qsc-input-field min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-preset-body-strong shadow-none outline-none placeholder:text-text-placeholder focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed"
            {...props}
          />
          {clearable && value && !disabled && (
            <button
              type="button"
              className="flex size-5 items-center justify-center rounded-full text-text-subtle hover:bg-gray-200 sm:size-6"
              onClick={onClear}
              aria-label="입력값 지우기"
            >
              <X aria-hidden className="size-4" />
            </button>
          )}
          {isPassword && (
            <button
              type="button"
              className="flex size-5 items-center justify-center rounded-full text-text-subtle hover:bg-gray-200 sm:size-6"
              onClick={() => setVisible((next) => !next)}
              aria-label={visible ? '비밀번호 숨기기' : '비밀번호 보기'}
            >
              {visible ? (
                <EyeOff aria-hidden className="size-4" />
              ) : (
                <Eye aria-hidden className="size-4" />
              )}
            </button>
          )}
          {trailing}
        </div>
        {error ? (
          <p id={`${inputId}-error`} className="text-preset-helper text-danger">
            {error}
          </p>
        ) : helperText ? (
          <p id={`${inputId}-helper`} className="text-preset-helper text-text-subtle">
            {helperText}
          </p>
        ) : null}
      </div>
    )
  },
)

Input.displayName = 'Input'
